<script setup>
import { Link, router } from '@inertiajs/vue3'
import { ref } from 'vue'
import Pagination from '@/Shared/Pagination.vue'
import Portrait from '@/Shared/Portrait.vue'
import SearchInput from '@/Shared/SearchInput.vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  students: {
    type: Object,
    default: () => {}
  },
  filters: {
    type: Object,
    default: () => ({
      search: '',
      level: 'all'
    })
  },
  levels: {
    type: Array,
    default: () => []
  }
})

const search = ref(props.filters.search)
const selectedLevels = ref(
  props.filters.levels ? props.filters.levels.split(',').filter((level) => level !== '') : []
)
const showFilters = ref(false)

const breadcrumbs = [{ label: 'Clases' }, { label: 'Alumnos' }]

function isColorLight(hex) {
  const r = parseInt(hex.substring(1, 3), 16)
  const g = parseInt(hex.substring(3, 5), 16)
  const b = parseInt(hex.substring(5, 7), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5
}

// Manejar la selección de niveles
function toggleLevelFilter(level) {
  const index = selectedLevels.value.indexOf(level)

  if (index === -1) {
    selectedLevels.value.push(level)
  } else {
    selectedLevels.value.splice(index, 1)
  }

  router.get(
    '/students',
    {
      search: search.value,
      levels: selectedLevels.value.join(',')
    },
    {
      preserveState: true,
      preserveScroll: true,
      only: ['students']
    }
  )
}

function clearFilters() {
  selectedLevels.value = []
  router.get(
    '/students',
    {
      search: search.value,
      levels: ''
    },
    {
      preserveState: true,
      preserveScroll: true,
      only: ['students']
    }
  )
}
</script>

<template>
  <Portrait title="Alumnos" subtitle="Gestiona los alumnos del club" :breadcrumbs="breadcrumbs">
    <!-- Barra de herramientas superior -->
    <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <SearchInput
        v-model="search"
        class="w-full sm:w-64"
        placeholder="Buscar alumnos..."
        url="/students"
        :debounce-time="250"
        :only="['students']"
      />

      <div class="flex space-x-3 items-stretch">
        <button
          class="flex items-center px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          @click="showFilters = !showFilters"
        >
          <Icon name="Filter" class="h-4 w-4 mr-2" />
          <span>Filtros</span>
          <span
            class="ml-1 bg-indigo-100 text-indigo-600 rounded-full px-2 py-0.5 text-xs font-semibold"
            >{{ selectedLevels.length }}</span
          >
        </button>

        <Link class="btn-indigo flex items-center justify-center" href="/students/create">
          <Icon name="Plus" class="h-5 w-5 mr-2" />
          <span>Nuevo alumno</span>
        </Link>
      </div>
    </div>

    <!-- Filtros de niveles -->
    <div
      v-if="showFilters"
      class="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200 transition-all"
    >
      <h3 class="font-medium text-gray-700 mb-3">Filtrar por nivel</h3>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="level in props.levels"
          :key="level"
          :class="[
            'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            selectedLevels.includes(level)
              ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
              : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
          ]"
          @click="toggleLevelFilter(level)"
        >
          {{ level.charAt(0).toUpperCase() + level.slice(1) }}
        </button>
        <button
          v-if="selectedLevels.length > 0"
          class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors bg-red-100 text-red-700 border border-red-200 hover:bg-red-200"
          @click="clearFilters"
        >
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Lista de alumnos -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <!-- Vista de lista avanzada -->
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left font-medium text-gray-700 bg-gray-50 border-b">
              <th class="px-6 py-3">Alumno</th>
              <th class="px-6 py-3">Contacto</th>
              <th class="px-6 py-3">Niveles</th>
              <th class="px-6 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="student in students.data"
              :key="student.id"
              class="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
              @click="$inertia.visit(`/students/${student.id}/edit`)"
            >
              <!-- Columna de alumno con avatar -->
              <td class="px-6 py-4">
                <div class="flex items-center">
                  <div
                    class="bg-indigo-100 text-indigo-700 rounded-full h-10 w-10 flex items-center justify-center font-bold mr-3"
                  >
                    {{ student.player.name.charAt(0).toUpperCase()
                    }}{{ student.player.lastname.charAt(0).toUpperCase() }}
                  </div>
                  <div>
                    <div class="font-medium text-gray-900">
                      {{ student.player.name }} {{ student.player.lastname }}
                    </div>
                    <div class="text-sm text-gray-500">ID: {{ student.id }}</div>
                  </div>
                </div>
              </td>

              <!-- Columna de contacto -->
              <td class="px-6 py-4">
                <div class="flex items-center text-gray-700">
                  <Icon name="Phone" class="h-4 w-4 mr-2 text-gray-500" />
                  <span>{{ student.player.phone }}</span>
                </div>
              </td>

              <!-- Columna de niveles -->
              <td class="px-6 py-4">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="level in student.levels"
                    :key="level.id"
                    class="px-2 py-1 inline-flex text-xs leading-4 font-medium rounded-full"
                    :style="`background-color: ${level.color}; color: ${
                      isColorLight(level.color) ? 'black' : 'white'
                    };`"
                  >
                    {{ level.name }}
                  </span>
                </div>
              </td>

              <!-- Columna de acciones -->
              <td class="px-6 py-4 text-right">
                <span class="text-indigo-600 font-medium text-sm inline-flex items-center">
                  <span>Editar</span>
                  <Icon name="Pencil" class="h-5 w-5 ml-1" />
                </span>
              </td>
            </tr>

            <!-- Estado vacío -->
            <tr v-if="students.data.length === 0">
              <td class="px-6 py-10 text-center text-gray-500 border-b" colspan="4">
                <div class="flex flex-col items-center justify-center">
                  <Icon name="Warning" class="h-10 w-10 text-gray-400 mb-2" />
                  <p class="text-lg font-medium">
                    {{
                      selectedLevels.length > 0
                        ? `No hay alumnos en los niveles seleccionados`
                        : 'No hay alumnos todavía'
                    }}
                  </p>
                  <p class="text-sm mt-1">
                    {{
                      selectedLevels.length > 0
                        ? 'Prueba con otros filtros o crea un nuevo alumno'
                        : 'Crea un nuevo alumno para comenzar'
                    }}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Pagination class="mt-6" :links="students.links" :preserve-state="true" :only="['students']" />
  </Portrait>
</template>
